import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Calculate as CalculateIcon } from '@mui/icons-material';
import { LoadScript } from '@react-google-maps/api';
import api from '../../services/api';
import {
  calculateDeliveryFee,
  formatFullAddress,
  loadFeeSettings,
  saveFeeSettings,
  simulateDeliveryFee,
} from '../../utils/deliveryFeeSimulator';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const mapsApiKey =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E';

export default function DeliveryFeeSimulatorSection() {
  const [organization, setOrganization] = useState(null);
  const [fleetVehicleCount, setFleetVehicleCount] = useState(null);
  const [feeSettings, setFeeSettings] = useState(loadFeeSettings);
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_address: '',
    delivery_city: '',
    delivery_state: 'WA',
    delivery_zip: '',
    vehicle_count: '1',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [orgRes, vehiclesRes] = await Promise.all([
          api.get('/organizations/organizations/profile/').catch(() => ({ data: null })),
          api.get('/vehicles/').catch(() => ({ data: [] })),
        ]);
        setOrganization(orgRes.data || null);
        const vehicles = Array.isArray(vehiclesRes.data)
          ? vehiclesRes.data
          : vehiclesRes.data?.results || [];
        setFleetVehicleCount(vehicles.length);
      } catch {
        setOrganization(null);
      }
    })();
  }, []);

  const homeAddress = organization ? formatFullAddress(organization) : '';
  const deliveryAddress = formatFullAddress(deliveryForm);

  const handleDeliveryChange = (e) => {
    setDeliveryForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setResult(null);
  };

  const handleFeeSettingChange = (e) => {
    const next = { ...feeSettings, [e.target.name]: e.target.value };
    setFeeSettings(next);
    saveFeeSettings(next);
    setResult(null);
  };

  const handleCalculate = async () => {
    setError('');
    setResult(null);

    if (!mapsReady) {
      setError('Google Maps is still loading. Please wait a moment and try again.');
      return;
    }
    if (!homeAddress) {
      setError('Set your organization address under Organization before using the simulator.');
      return;
    }
    if (!deliveryForm.delivery_address || !deliveryForm.delivery_city || !deliveryForm.delivery_state) {
      setError('Enter the delivery street, city, and state.');
      return;
    }

    setLoading(true);
    try {
      const simulation = await simulateDeliveryFee({
        homeAddress,
        deliveryAddress,
        vehicleCount: deliveryForm.vehicle_count,
        baseFee: feeSettings.baseFee,
        perMileRate: feeSettings.perMileRate,
        perVehiclePrice: feeSettings.perVehiclePrice,
      });
      setResult(simulation);
    } catch (err) {
      setError(err.message || 'Failed to calculate delivery fee.');
    } finally {
      setLoading(false);
    }
  };

  const feePreview =
    result?.fee ||
    (result?.driving?.miles != null
      ? calculateDeliveryFee({
          distanceMiles: result.driving.miles,
          vehicleCount: deliveryForm.vehicle_count,
          baseFee: feeSettings.baseFee,
          perMileRate: feeSettings.perMileRate,
          perVehiclePrice: feeSettings.perVehiclePrice,
        })
      : null);

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        Delivery Fee Simulator
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Estimates delivery fee from driving distance between your organization (home) and the
        delivery address, multiplied by the number of vehicles on the job.
      </Typography>

      {!mapsApiKey && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to enable distance calculations.
        </Alert>
      )}

      <LoadScript googleMapsApiKey={mapsApiKey} onLoad={() => setMapsReady(true)}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Home (organization)
          </Typography>
          {organization ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {homeAddress || 'Organization address is incomplete.'}
            </Typography>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              No organization profile found. Add your company address under{' '}
              <strong>Organization</strong> in the sidebar.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Delivery location
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="delivery_address"
                label="Street address"
                value={deliveryForm.delivery_address}
                onChange={handleDeliveryChange}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                name="delivery_city"
                label="City"
                value={deliveryForm.delivery_city}
                onChange={handleDeliveryChange}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  name="delivery_state"
                  label="State"
                  value={deliveryForm.delivery_state}
                  onChange={handleDeliveryChange}
                >
                  {US_STATES.map((st) => (
                    <MenuItem key={st} value={st}>
                      {st}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                name="delivery_zip"
                label="ZIP"
                value={deliveryForm.delivery_zip}
                onChange={handleDeliveryChange}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Fee formula settings
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Fee = (base + miles × per mile + price per vehicle) × vehicles. Saved in this browser.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="baseFee"
                label="Base fee ($)"
                type="number"
                value={feeSettings.baseFee}
                onChange={handleFeeSettingChange}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="perMileRate"
                label="Per mile ($)"
                type="number"
                value={feeSettings.perMileRate}
                onChange={handleFeeSettingChange}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="perVehiclePrice"
                label="Price per vehicle ($)"
                type="number"
                value={feeSettings.perVehiclePrice}
                onChange={handleFeeSettingChange}
                inputProps={{ min: 0, step: '0.01' }}
                helperText="Added for each vehicle on the job"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                name="vehicle_count"
                label="Vehicles on job"
                type="number"
                value={deliveryForm.vehicle_count}
                onChange={handleDeliveryChange}
                inputProps={{ min: 1, step: 1 }}
                helperText={
                  fleetVehicleCount != null
                    ? `${fleetVehicleCount} vehicle${fleetVehicleCount === 1 ? '' : 's'} in fleet`
                    : undefined
                }
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
            onClick={handleCalculate}
            disabled={loading || !mapsReady}
          >
            {loading ? 'Calculating…' : 'Calculate delivery fee'}
          </Button>

          {result && feePreview && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Suggested delivery fee: ${feePreview.totalFee.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Driving distance:</strong> {result.driving.milesText}
                {result.driving.durationText ? ` (${result.driving.durationText} one way)` : ''}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Distance (numeric):</strong> {feePreview.distanceMiles.toFixed(1)} mi
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Per vehicle:</strong> ${feePreview.baseFee.toFixed(2)} base + $
                {feePreview.distanceComponent.toFixed(2)} distance
                {feePreview.perVehiclePrice > 0
                  ? ` + $${feePreview.perVehiclePrice.toFixed(2)} per vehicle`
                  : ''}{' '}
                = ${feePreview.perVehicleFee.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Vehicles:</strong> {feePreview.vehicleCount} × $
                {feePreview.perVehicleFee.toFixed(2)} = ${feePreview.totalFee.toFixed(2)}
              </Typography>
              {result.delivery?.formatted && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Delivery geocoded as: {result.delivery.formatted}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </LoadScript>
    </Paper>
  );
}
